/**
 * Location Scheme Editor.
 *
 * Dynamic form for creating and editing location naming schemes with
 * industry-standard warehouse naming conventions. Features:
 *
 * - One-click preset templates for common warehouse patterns
 * - Add-segment dropdown with smart preset defaults (Zone, Aisle, Rack, etc.)
 * - Drag-and-drop segment reordering via grip handles
 * - Industry guidance text (serpentine numbering, odd/even, start-at-10)
 * - Live preview of generated location labels
 *
 * Based on warehouse naming best practices from:
 * - ID Label Inc (numbering schemes)
 * - Clearly Inventory (naming conventions)
 * - ShipHero (location types & naming)
 * - Datalinx (bin/shelf labeling)
 * - ScanForce (numbering scheme implementation)
 */

import { useState, useMemo, useCallback } from "react";
import {
  Plus,
  Trash2,
  GripVertical,
  Eye,
  Zap,
  Sparkles,
  Check,
  Info,
} from "lucide-react";
import type { SegmentDefinition } from "@/types/locations";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";

interface LocationSchemeEditorProps {
  initialName?: string;
  initialDescription?: string;
  initialSegments?: SegmentDefinition[];
  initialSeparator?: string;
  onSave: (data: {
    name: string;
    description?: string;
    segments: SegmentDefinition[];
    separator: string;
  }) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

// ---------------------------------------------------------------------------
// Hierarchy Levels — industry-standard warehouse location segments
// ---------------------------------------------------------------------------

interface HierarchyLevel {
  value: string;
  label: string;
  hint: string;
  defaults: Partial<SegmentDefinition>;
}

const HIERARCHY_LEVELS: HierarchyLevel[] = [
  {
    value: "zone",
    label: "Zone / Area",
    hint: "Broadest grouping \u2014 identifies major facility areas (dry goods, freezer, receiving, bulk).",
    defaults: {
      name: "Zone",
      type: "letter",
      start: "A",
      end: "D",
      padding: 0,
    },
  },
  {
    value: "aisle",
    label: "Aisle",
    hint: "Corridor between rack rows. Tip: Start at 01 (or 10 for expansion room). Use odd/even for left/right sides.",
    defaults: { name: "Aisle", type: "number", start: 1, end: 50, padding: 2 },
  },
  {
    value: "rack",
    label: "Rack / Bay",
    hint: "Vertical shelving unit along an aisle. Number from the dock end. Change numbering at cross aisles.",
    defaults: { name: "Bay", type: "number", start: 1, end: 20, padding: 2 },
  },
  {
    value: "level",
    label: "Level / Shelf",
    hint: "Horizontal tier within a rack. A = ground level, ascending. Allows adding upper levels without relabeling.",
    defaults: {
      name: "Level",
      type: "letter",
      start: "A",
      end: "F",
      padding: 0,
    },
  },
  {
    value: "position",
    label: "Position / Bin",
    hint: "Specific slot on a shelf. Number left-to-right as you face the rack. Two digits (01\u201399) is standard.",
    defaults: {
      name: "Position",
      type: "number",
      start: 1,
      end: 4,
      padding: 2,
    },
  },
  {
    value: "section",
    label: "Section",
    hint: "A named subdivision. Common for parking lots (A\u2013Z) or storage areas.",
    defaults: {
      name: "Section",
      type: "letter",
      start: "A",
      end: "Z",
      padding: 0,
    },
  },
  {
    value: "custom",
    label: "Custom",
    hint: "Free-form segment \u2014 define your own name and range.",
    defaults: { name: "", type: "number", start: 1, end: 10, padding: 2 },
  },
];

// ---------------------------------------------------------------------------
// Preset Templates — one-click starting points
// ---------------------------------------------------------------------------

interface SchemePreset {
  name: string;
  description: string;
  example: string;
  separator: string;
  segments: SegmentDefinition[];
}

const SCHEME_PRESETS: SchemePreset[] = [
  {
    name: "Pallet Rack",
    description:
      "Standard rack storage: Aisle \u2192 Bay \u2192 Level \u2192 Position",
    example: "01-03-B-02",
    separator: "-",
    segments: [
      { name: "Aisle", type: "number", start: 1, end: 50, padding: 2 },
      { name: "Bay", type: "number", start: 1, end: 20, padding: 2 },
      { name: "Level", type: "letter", start: "A", end: "F" },
      { name: "Position", type: "number", start: 1, end: 4, padding: 2 },
    ],
  },
  {
    name: "Parking Lot",
    description: "Section letter + numbered spaces",
    example: "A-042",
    separator: "-",
    segments: [
      { name: "Section", type: "letter", start: "A", end: "Z" },
      { name: "Space", type: "number", start: 1, end: 200, padding: 3 },
    ],
  },
  {
    name: "Storage Bins",
    description: "Zone prefix + sequential bin numbers",
    example: "BIN-0042",
    separator: "-",
    segments: [
      { name: "Zone", type: "fixed", values: ["BIN"] },
      { name: "Bin", type: "number", start: 1, end: 500, padding: 4 },
    ],
  },
  {
    name: "Floor Staging",
    description: "Zone prefix + row + spot",
    example: "FL-03-12",
    separator: "-",
    segments: [
      { name: "Zone", type: "fixed", values: ["FL"] },
      { name: "Row", type: "number", start: 1, end: 10, padding: 2 },
      { name: "Spot", type: "number", start: 1, end: 20, padding: 2 },
    ],
  },
  {
    name: "Cold Storage",
    description: "Temperature prefix + zone + row",
    example: "FRZ-B-07",
    separator: "-",
    segments: [
      { name: "Temp", type: "fixed", values: ["FRZ", "CHL"] },
      { name: "Zone", type: "letter", start: "A", end: "D" },
      { name: "Row", type: "number", start: 1, end: 20, padding: 2 },
    ],
  },
  {
    name: "Shelving Unit",
    description: "Section + shelf + position",
    example: "C-04-03",
    separator: "-",
    segments: [
      { name: "Section", type: "letter", start: "A", end: "Z" },
      { name: "Shelf", type: "number", start: 1, end: 9, padding: 2 },
      { name: "Position", type: "number", start: 1, end: 6, padding: 2 },
    ],
  },
  {
    name: "Simple Sequential",
    description: "Prefix + sequential number",
    example: "LOC-0001",
    separator: "-",
    segments: [
      { name: "Prefix", type: "fixed", values: ["LOC"] },
      { name: "Number", type: "number", start: 1, end: 9999, padding: 4 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Segment Types
// ---------------------------------------------------------------------------

const SEGMENT_TYPES = [
  { value: "number", label: "Number Range", hint: "e.g., 01\u201350" },
  { value: "letter", label: "Letter Range", hint: "e.g., A\u2013Z" },
  { value: "fixed", label: "Fixed Values", hint: "e.g., BIN, FRZ" },
] as const;

// ---------------------------------------------------------------------------
// Pure Functions
// ---------------------------------------------------------------------------

/**
 * Sample values for a single segment, used only to illustrate the label
 * *pattern*. A scheme is structure-only (no ranges), so number/letter segments
 * use small placeholder ranges; fixed segments show their first couple values.
 */
function sampleSegment(seg: SegmentDefinition): string[] {
  const prefix = seg.prefix || "";
  if (seg.type === "number") {
    const pad = seg.padding ?? 0;
    return [1, 2].map((i) => `${prefix}${String(i).padStart(pad, "0")}`);
  }
  if (seg.type === "letter") {
    return ["A", "B"].map((c) => `${prefix}${c}`);
  }
  if (seg.type === "fixed") {
    const vals = (seg.values ?? []).slice(0, 2);
    return vals.length ? vals.map((v) => `${prefix}${v}`) : [`${prefix}…`];
  }
  return [];
}

/**
 * A few sample labels showing the scheme's structure (prefix / padding /
 * separator), built from placeholder values — never the full set. Concrete
 * ranges and counts are chosen per storage area when the scheme is applied.
 */
function structurePreview(
  segments: SegmentDefinition[],
  separator: string,
): string[] {
  if (segments.length === 0) return [];
  const expanded = segments.map(sampleSegment);
  if (expanded.some((e) => e.length === 0)) return [];

  const MAX = 8;
  const result: string[] = [];
  const indices = new Array(expanded.length).fill(0) as number[];
  while (result.length < MAX) {
    result.push(indices.map((idx, i) => expanded[i]![idx]!).join(separator));
    let carry = true;
    for (let i = indices.length - 1; i >= 0 && carry; i--) {
      indices[i]!++;
      if (indices[i]! < expanded[i]!.length) {
        carry = false;
      } else {
        indices[i] = 0;
      }
    }
    if (carry) break;
  }
  return result;
}

const selectClass =
  "mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm";

// ---------------------------------------------------------------------------
// Natural-Language Parser — "describe what you need"
// ---------------------------------------------------------------------------

const NOUN_DEFAULTS: Record<
  string,
  { name: string; type: "number" | "letter"; prefix?: string }
> = {
  space: { name: "Space", type: "number", prefix: "P" },
  spot: { name: "Spot", type: "number", prefix: "S" },
  stall: { name: "Stall", type: "number", prefix: "S" },
  bin: { name: "Bin", type: "number", prefix: "BIN" },
  slot: { name: "Slot", type: "number", prefix: "LOC" },
  position: { name: "Position", type: "number" },
  location: { name: "Location", type: "number", prefix: "LOC" },
  unit: { name: "Unit", type: "number", prefix: "U" },
  bay: { name: "Bay", type: "number" },
  row: { name: "Row", type: "number" },
  aisle: { name: "Aisle", type: "number" },
  rack: { name: "Rack", type: "number" },
  zone: { name: "Zone", type: "letter" },
  area: { name: "Area", type: "letter" },
  section: { name: "Section", type: "letter" },
  level: { name: "Level", type: "letter" },
  shelf: { name: "Shelf", type: "number" },
  tier: { name: "Tier", type: "letter" },
  lane: { name: "Lane", type: "number" },
};

interface DescribeResult {
  segments: SegmentDefinition[];
  separator: string;
  summary: string;
  suggestedName: string;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function parseDescription(text: string): DescribeResult | null {
  const lower = text.toLowerCase().trim();
  if (!lower || lower.length < 3) return null;

  const pairRegex = /(\d+)\s+([a-z]+?)s?\b/g;
  const pairs: Array<{ count: number; noun: string }> = [];
  let m;
  while ((m = pairRegex.exec(lower)) !== null) {
    pairs.push({ count: parseInt(m[1]!), noun: m[2]! });
  }
  if (pairs.length === 0) return null;

  if (pairs.length === 1) {
    const p = pairs[0]!;
    const info = NOUN_DEFAULTS[p.noun];
    const prefix = info?.prefix || capitalize(p.noun).slice(0, 3).toUpperCase();
    const pad = p.count >= 1000 ? 4 : p.count >= 100 ? 3 : 2;
    return {
      segments: [
        { name: "Prefix", type: "fixed", values: [prefix] },
        {
          name: info?.name || capitalize(p.noun),
          type: "number",
          start: 1,
          end: p.count,
          padding: pad,
        },
      ],
      separator: "-",
      summary: `${p.count} ${info?.name.toLowerCase() || p.noun}s`,
      suggestedName: `${capitalize(info?.name || p.noun)} Layout`,
    };
  }

  const segments: SegmentDefinition[] = pairs.map((p) => {
    const info = NOUN_DEFAULTS[p.noun];
    const segName = info?.name || capitalize(p.noun);
    const segType = info?.type || "number";
    if (segType === "letter") {
      const endChar = String.fromCharCode(64 + Math.min(p.count, 26));
      return {
        name: segName,
        type: "letter" as const,
        start: "A",
        end: endChar,
      };
    }
    const pad = p.count >= 1000 ? 4 : p.count >= 100 ? 3 : 2;
    return {
      name: segName,
      type: "number" as const,
      start: 1,
      end: p.count,
      padding: pad,
    };
  });

  const parts = pairs.map((p) => {
    const info = NOUN_DEFAULTS[p.noun];
    return `${p.count} ${info?.name.toLowerCase() || p.noun}s`;
  });
  const total = segments.reduce((acc, seg) => {
    if (seg.type === "letter") {
      return (
        acc *
        (String(seg.end ?? "A").charCodeAt(0) -
          String(seg.start ?? "A").charCodeAt(0) +
          1)
      );
    }
    return acc * ((seg.end as number) - (seg.start as number) + 1);
  }, 1);

  return {
    segments,
    separator: "-",
    summary: `${parts.join(" × ")} = ${total.toLocaleString()} locations`,
    suggestedName:
      parts.map((p) => capitalize(p.split(" ")[1] || "")).join(" + ") +
      " Layout",
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function LocationSchemeEditor({
  initialName = "",
  initialDescription = "",
  initialSegments,
  initialSeparator = "-",
  onSave,
  onCancel,
  isSaving = false,
}: LocationSchemeEditorProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [separator, setSeparator] = useState(initialSeparator);
  const [segments, setSegments] = useState<SegmentDefinition[]>(
    initialSegments && initialSegments.length > 0 ? initialSegments : [],
  );
  const [showPreview, setShowPreview] = useState(true);
  const [showPresets, setShowPresets] = useState(
    !initialName && (!initialSegments || initialSegments.length === 0),
  );
  const [describeText, setDescribeText] = useState("");
  const describeResult = useMemo(
    () => parseDescription(describeText),
    [describeText],
  );
  const applyDescribeResult = useCallback(() => {
    if (!describeResult) return;
    setSegments(describeResult.segments);
    setSeparator(describeResult.separator);
    if (!name) setName(describeResult.suggestedName);
    setShowPresets(false);
    setDescribeText("");
  }, [describeResult, name]);

  const previewLabels = useMemo(
    () => structurePreview(segments, separator),
    [segments, separator],
  );

  const isValid =
    name.trim().length > 0 &&
    segments.length > 0 &&
    segments.every((s) => s.name.trim().length > 0);

  const applyPreset = (preset: SchemePreset) => {
    setName(preset.name);
    setDescription(preset.description);
    setSeparator(preset.separator);
    setSegments(preset.segments.map((s) => ({ ...s })));
    setShowPresets(false);
  };

  const updateSegment = useCallback(
    (index: number, updates: Partial<SegmentDefinition>) => {
      setSegments((prev) =>
        prev.map((seg, i) => {
          if (i !== index) return seg;
          const updated = { ...seg, ...updates };
          // Reset type-specific fields when type changes.
          if (updates.type && updates.type !== seg.type) {
            if (updates.type === "number") {
              return {
                ...updated,
                start: 1,
                end: 10,
                padding: 2,
                values: undefined,
              };
            }
            if (updates.type === "letter") {
              return {
                ...updated,
                start: "A",
                end: "Z",
                padding: 0,
                values: undefined,
              };
            }
            if (updates.type === "fixed") {
              return {
                ...updated,
                start: undefined,
                end: undefined,
                padding: 0,
                values: [""],
              };
            }
          }
          return updated;
        }),
      );
    },
    [],
  );

  // --- Add-segment menu ---
  const [showAddMenu, setShowAddMenu] = useState(false);

  const addSegmentFromLevel = useCallback((levelValue: string) => {
    const level = HIERARCHY_LEVELS.find((l) => l.value === levelValue);
    if (!level) return;
    setSegments((prev) => [
      ...prev,
      {
        name: "",
        type: "number",
        start: 1,
        end: 10,
        padding: 2,
        ...level.defaults,
      } as SegmentDefinition,
    ]);
    setShowAddMenu(false);
  }, []);

  // --- Drag-and-drop reordering ---
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggableIdx, setDraggableIdx] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === toIndex) {
        setDragIndex(null);
        setDragOverIndex(null);
        return;
      }
      setSegments((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(toIndex, 0, moved!);
        return next;
      });
      setDragIndex(null);
      setDragOverIndex(null);
      setDraggableIdx(null);
    },
    [dragIndex],
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
    setDraggableIdx(null);
  }, []);

  const removeSegment = (index: number) =>
    setSegments((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    onSave({
      name,
      description: description || undefined,
      segments,
      separator,
    });
  };

  // Detect which hierarchy level a segment matches (for contextual hints).
  const detectHierarchyLevel = (seg: SegmentDefinition): string => {
    const n = seg.name.toLowerCase();
    if (n.includes("zone") || n.includes("area") || n.includes("temp"))
      return "zone";
    if (n.includes("aisle")) return "aisle";
    if (n.includes("rack") || n.includes("bay")) return "rack";
    if (n.includes("level") || n.includes("shelf") || n.includes("tier"))
      return "level";
    if (n.includes("position") || n.includes("bin") || n.includes("slot"))
      return "position";
    if (n.includes("section") || n.includes("lot") || n.includes("space"))
      return "section";
    return "custom";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Preset Templates */}
      {showPresets && (
        <Card className="bg-brand-50/50 dark:bg-brand-900/10 border-brand-200 dark:border-brand-800">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-brand-600" />
            <h4 className="text-sm font-semibold text-[var(--text-primary)]">
              Start from Template
            </h4>
            <button
              type="button"
              onClick={() => setShowPresets(false)}
              className="ml-auto text-[10px] text-[var(--text-tertiary)] hover:underline"
            >
              Build from scratch
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SCHEME_PRESETS.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => applyPreset(preset)}
                className="text-left rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--surface-base)] px-3 py-2.5 hover:border-brand-400 hover:bg-brand-50/50 dark:hover:bg-brand-900/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[var(--text-primary)]">
                    {preset.name}
                  </span>
                  <span className="text-[10px] font-mono text-brand-600 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 px-1.5 py-0.5 rounded">
                    {preset.example}
                  </span>
                </div>
                <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                  {preset.description}
                </p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Describe your space — smart natural-language input */}
      {segments.length === 0 && !showPresets && (
        <div className="rounded-[var(--radius-lg)] border border-brand-200 dark:border-brand-800 bg-brand-50/30 dark:bg-brand-900/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-brand-600 dark:text-brand-300" />
            <span className="text-xs font-semibold text-[var(--text-primary)]">
              Describe what you need
            </span>
          </div>
          <input
            value={describeText}
            onChange={(e) => setDescribeText(e.target.value)}
            placeholder='e.g., "50 parking spaces", "10 aisles with 8 bays and 5 levels"'
            className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm placeholder:text-[var(--text-tertiary)]"
          />
          {describeText.length >= 3 && (
            <div className="mt-2">
              {describeResult ? (
                <div className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-[var(--color-success)] shrink-0" />
                  <span className="text-xs text-[var(--text-primary)]">
                    Understood: <strong>{describeResult.summary}</strong>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyDescribeResult}
                    className="ml-auto"
                  >
                    <Zap className="h-3 w-3" /> Apply
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-[var(--text-tertiary)] flex items-center gap-1.5">
                  <Info className="h-3 w-3" />
                  Try: "50 spots", "4 rows of 25 spaces", "10 aisles, 8 bays, 5
                  levels"
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scheme metadata */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Scheme Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Pallet Rack 3-Tier"
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
        />
        <div>
          <Input
            label="Separator"
            value={separator}
            onChange={(e) => setSeparator(e.target.value)}
            placeholder="-"
            maxLength={5}
          />
          <p className="mt-0.5 text-[9px] text-[var(--text-tertiary)]">
            Hyphens (-) are the most common. Some systems omit separators for
            shorter labels.
          </p>
        </div>
      </div>

      {/* Segments */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-[var(--text-primary)]">
            Segments ({segments.length})
          </h4>
          <div className="flex items-center gap-2">
            {!showPresets && segments.length === 0 && (
              <button
                type="button"
                onClick={() => setShowPresets(true)}
                className="text-[10px] text-brand-600 hover:underline"
              >
                Use a template
              </button>
            )}
            {segments.length < 6 && (
              <div className="relative">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowAddMenu(!showAddMenu)}
                >
                  <Plus className="h-3.5 w-3.5" /> Add Segment
                </Button>
                {showAddMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowAddMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-20 w-72 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-raised)] shadow-lg overflow-hidden">
                      <div className="px-3 py-2 border-b border-[var(--border-subtle)]">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                          Segment Presets
                        </span>
                      </div>
                      {HIERARCHY_LEVELS.filter((l) => l.value !== "custom").map(
                        (level) => (
                          <button
                            key={level.value}
                            type="button"
                            onClick={() => addSegmentFromLevel(level.value)}
                            className="w-full text-left px-3 py-2 hover:bg-[var(--surface-sunken)] transition-colors"
                          >
                            <div className="text-xs font-medium text-[var(--text-primary)]">
                              {level.label}
                            </div>
                            <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5 line-clamp-1">
                              {level.hint.split(".")[0]}.
                            </div>
                          </button>
                        ),
                      )}
                      <div className="border-t border-[var(--border-subtle)]" />
                      <button
                        type="button"
                        onClick={() => addSegmentFromLevel("custom")}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--surface-sunken)] transition-colors"
                      >
                        <div className="text-xs font-medium text-[var(--text-primary)]">
                          Blank Segment
                        </div>
                        <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                          Define your own name and range.
                        </div>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="text-[10px] text-[var(--text-tertiary)] mb-3">
          Each segment generates one part of the location label. Drag segments
          to reorder — first segment appears first in the label.
        </p>

        <div className="space-y-3">
          {segments.map((seg, idx) => {
            const detectedLevel = detectHierarchyLevel(seg);
            const levelInfo = HIERARCHY_LEVELS.find(
              (l) => l.value === detectedLevel,
            );
            return (
              <Card
                key={idx}
                padding="sm"
                className={`bg-[var(--surface-sunken)] transition-opacity ${dragIndex === idx ? "opacity-40" : ""} ${dragOverIndex === idx && dragIndex !== idx ? "ring-2 ring-[var(--color-interactive-primary)]" : ""}`}
                draggable={draggableIdx === idx}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <div className="flex items-start gap-3">
                  <GripVertical
                    className="h-4 w-4 mt-8 shrink-0 text-[var(--text-tertiary)] cursor-grab active:cursor-grabbing"
                    aria-label="Drag to reorder"
                    onMouseDown={() => setDraggableIdx(idx)}
                    onMouseUp={() => setDraggableIdx(null)}
                  />
                  <div className="flex-1 space-y-3">
                    {/* Row 1: Name + Type + Prefix */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Input
                        label="Segment Name"
                        value={seg.name}
                        onChange={(e) =>
                          updateSegment(idx, { name: e.target.value })
                        }
                        placeholder="e.g., Aisle"
                        required
                      />

                      <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)]">
                          Type
                        </label>
                        <select
                          value={seg.type}
                          onChange={(e) =>
                            updateSegment(idx, {
                              type: e.target.value as SegmentDefinition["type"],
                            })
                          }
                          className={selectClass}
                        >
                          {SEGMENT_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Input
                        label="Prefix"
                        value={seg.prefix ?? ""}
                        onChange={(e) =>
                          updateSegment(idx, { prefix: e.target.value })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    {/* Row 2: Type-specific structure. Ranges (start/end) are
                        NOT set here — they're chosen per storage area when the
                        scheme is applied. */}
                    {seg.type === "number" && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                        <Input
                          label="Pad Digits"
                          type="number"
                          value={String(seg.padding ?? 0)}
                          min={0}
                          max={10}
                          onChange={(e) =>
                            updateSegment(idx, {
                              padding: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                        <div className="sm:col-span-3 flex items-end">
                          <span className="text-[10px] text-[var(--text-tertiary)] pb-2">
                            Numeric range is set per storage area when the
                            scheme is applied.
                          </span>
                        </div>
                      </div>
                    )}

                    {seg.type === "letter" && (
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        Letter range is set per storage area when the scheme is
                        applied.
                      </p>
                    )}

                    {seg.type === "fixed" && (
                      <Input
                        label="Values (comma-separated)"
                        value={(seg.values ?? []).join(", ")}
                        onChange={(e) =>
                          updateSegment(idx, {
                            values: e.target.value
                              .split(",")
                              .map((v) => v.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="BIN, SHELF, FRZ, CHL"
                      />
                    )}

                    {/* Hierarchy hint */}
                    {levelInfo && levelInfo.value !== "custom" && (
                      <p className="text-[9px] text-[var(--text-tertiary)] italic">
                        {levelInfo.hint}
                      </p>
                    )}
                  </div>

                  {segments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSegment(idx)}
                      className="mt-8 shrink-0 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}

          {segments.length === 0 && !showPresets && (
            <Card className="border-dashed">
              <div className="flex flex-col items-center py-6 text-center">
                <p className="text-sm text-[var(--text-secondary)]">
                  No segments defined
                </p>
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  Add segments to define how locations are labeled, or use a
                  template above.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addSegmentFromLevel("custom")}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Segment
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowPresets(true)}
                  >
                    <Zap className="h-3.5 w-3.5" /> Use Template
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Preview */}
      {segments.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Label pattern
              <span className="text-xs font-normal text-[var(--text-tertiary)]">
                (example — ranges are set per storage area)
              </span>
            </h4>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-xs text-brand-600 hover:underline"
            >
              {showPreview ? "Hide" : "Show"}
            </button>
          </div>

          {showPreview && previewLabels.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {previewLabels.map((label, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-md bg-[var(--surface-sunken)] px-2 py-0.5 text-xs font-mono text-[var(--text-secondary)]"
                >
                  {label}
                </span>
              ))}
              <span className="inline-flex items-center px-2 py-0.5 text-xs text-[var(--text-tertiary)]">
                …
              </span>
            </div>
          )}

          {showPreview && previewLabels.length === 0 && (
            <p className="text-xs text-[var(--text-tertiary)]">
              Configure segments above to see the label pattern.
            </p>
          )}
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isSaving} disabled={!isValid}>
          {initialName ? "Update Scheme" : "Create Scheme"}
        </Button>
      </div>
    </form>
  );
}

export default LocationSchemeEditor;
